import { Component, OnInit } from '@angular/core';
import { PatientChecklistService, CaseDetails, WeekDays } from './services/patient-checklist.service';
import { GetItemsParams } from '../attorney/service/attorney.model';
import { SharedService } from 'src/app/services/shared.service';
import { CaseType, Page, PatientCallingEnum, ToastType } from 'src/app/enums/enums';
import { AttorneyService } from '../attorney/service/attorney.service';
import { debounceTime, distinctUntilChanged, Subject, switchMap, takeUntil } from 'rxjs';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from 'src/app/services/toast.service';
import { IWeekDetails } from 'src/app/components/week-picker/week-picker.component';
import { DialogueService } from 'src/app/services/dialogue.service';
import { AddEditPatientDialogueComponent } from 'src/app/components/add-edit-patient-dialogue/add-edit-patient-dialogue.component';
import { VisitorStatusDialogueComponent } from 'src/app/components/visitor-status-dialogue/visitor-status-dialogue.component';

@Component({
  selector: 'app-patient-checklist',
  templateUrl: './patient-checklist.component.html',
  styleUrls: ['./patient-checklist.component.scss']
})
export class PatientChecklistComponent implements OnInit {
  public IsLoading = false;
  public PatientCheckList!: CaseDetails[];
  public TotalPatients!: number;
  public PaginationParams!: GetItemsParams;
  private Unsubscribe = new Subject<void>();
  public TodayDate = new Date();
  public Weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  public SelectedWeekDetails!: IWeekDetails;
  public SearchVal: string = '';
  private SearchSubject = new Subject<string>();
  public caseTypeOptions: { key: number; value: string }[] = [];
  PatientCallingStatus = Object.keys(PatientCallingEnum)
    .filter((key) => !isNaN(Number(key)))
    .map((key) => ({
      key: Number(key),
      value: PatientCallingEnum[Number(key)]
    }));
  constructor(
    private patientCheckListService: PatientChecklistService,
    public sharedService: SharedService,
    public attorneyService: AttorneyService,
    public toastService: ToastService,
    public dialogue: DialogueService
  ) {
    this.PaginationParams = {
      searchValue: '',
      searchDate: '2024-11-14',
      clinicId: this.sharedService.CurrentClinic.value || 0,
      pageNumber: 1,
      pageSize: Page.Size,
      sortOrder: 'asc',
      sortColumn: 'mriFacilityName',
      skip: 0,
      take: Number(this.sharedService.getData('records')) || Page.Size
    };
  }

  ngOnInit(): void {
    this.SearchSubject.pipe(
      debounceTime(500), // Wait for 300ms pause in input
      distinctUntilChanged(), // Ignore if the search term is the same as previous
      switchMap((term) => {
        this.PaginationParams.searchValue = term;
        this.GetPatientCheckList(this.PaginationParams);
        return '';
      })
    ).subscribe((results) => {
      console.log('results', results);
    });

    this.caseTypeOptions = Object.keys(CaseType)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({
        key: Number(key),
        value: CaseType[Number(key)]
      }));
  }

  SearchData() {
    this.SearchSubject.next(this.SearchVal);
  }

  ExportData(): void {
    const response = this.patientCheckListService.ExportPatientAttendance(this.PaginationParams);
    response.subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);

        // Create a temporary anchor element to trigger the download
        const link = document.createElement('a');
        link.href = url;
        link.download = 'WeeklyFrontDeskControlSheet.pdf'; // This should match the file name from the backend
        link.click();

        // Clean up the URL object
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error during file download', err);
      },
      complete: () => {}
    });
  }

  OpenEditPopup(id: number) {
    const data = {
      isEditPatient: true,
      PatinentId: id
    };
    const dialogRef = this.dialogue.Open(AddEditPatientDialogueComponent, { size: 'xl' }, data);
    dialogRef.result.then((res) => {
      if (res) {
        this.GetPatientCheckList(this.PaginationParams);
      }
    });
  }

  GetSelectedWeek(val: { start: NgbDate; end: NgbDate }) {
    const StartDayOfWeek = `${val.start.year}-${val.start.month}-${val.start.day}`;
    this.PaginationParams.searchDate = StartDayOfWeek;

    this.sharedService.CurrentClinic.pipe(takeUntil(this.Unsubscribe)).subscribe((val) => {
      this.PaginationParams.clinicId = Number(val);
      this.GetPatientCheckList(this.PaginationParams);
    });
  }

  GetWeekDetails(val: IWeekDetails) {
    this.SelectedWeekDetails = { ...val };
  }

  GetPatientCheckList(params: GetItemsParams) {
    if (params.clinicId !== 0) {
      this.IsLoading = true;
      this.patientCheckListService.GetPatientCheckList(params).subscribe({
        next: (res) => {
          this.PatientCheckList = [...res.result.patientCallingResponse];
          this.TotalPatients = res.result.total;
          this.PatientCheckList.map((patient) => {
            patient.caseName = CaseType[patient.caseType] || '';
            if (patient.attorneyId) {
              this.attorneyService.GetAttorney(patient.attorneyId).subscribe({
                next: (res: any) => (patient.attorneyName = res.result.attorneyName),
                error: (err) => console.log(err)
              });
            }
          });
        },
        error: (err) => {
          this.IsLoading = false;
        },
        complete: () => (this.IsLoading = false)
      });
    } else {
      this.toastService.showToast({
        message: 'Please, Select clinic from Dropdown in Header',
        type: ToastType.Info
      });
    }
  }

  GetMarkConfirmation(event: Event, item: CaseDetails, weekday: string) {
    event.preventDefault();
    const checkboxEle = event.target as HTMLInputElement;
    const confirmMeta: {
      alertTitle?: string;
      alertMessage?: string;
      alertAction?: {
        actionName: string;
        actionClass: string;
      };
    } = {};
    if (checkboxEle.checked) {
      confirmMeta.alertTitle = `Update status for ${item.lastName} ${item.firstName}`;
      confirmMeta.alertMessage = `Please select the status of <b class="text-black">${item.lastName} ${item.firstName}</b> as`;
      confirmMeta.alertAction = { actionName: 'Update', actionClass: '' };
      const dialogRef = this.dialogue.Open(VisitorStatusDialogueComponent, { size: 'md' }, { confirmMeta });
      dialogRef.result.then((res: Partial<{ status: Number | null; note: string }>) => {
        if (res) {
          this.IsLoading = true;
          if (event.target) checkboxEle.checked = true;
          this.UpdateVisited(item, weekday, res);
        }
      });
    } else {
      confirmMeta.alertTitle = 'Are you sure?';
      confirmMeta.alertMessage = `Are you sure you want to Remove status of <b class="text-black">${item.lastName} ${item.firstName}</b> ?`;
      confirmMeta.alertAction = {
        actionName: 'Yes, Remove',
        actionClass: 'btn-danger'
      };
      const getConfirmation = this.dialogue.GetConfirmation(confirmMeta.alertTitle, confirmMeta.alertMessage, confirmMeta.alertAction);
      getConfirmation.result.then((res) => {
        if (res) {
          if (event.target) checkboxEle.checked = false;
          this.UpdateVisited(item, weekday, { status: null, note: '' });
        }
      });
    }
  }

  UpdateVisited(info: CaseDetails, weekday: string, newVal: Partial<{ status: Number | null; note: string }>) {
    const reqBody: WeekDays = {
      patientId: info.patientId,
      monday: info.monday,
      mondayNote: info.mondayNote,
      tuesday: info.tuesday,
      tuesdayNote: info.tuesdayNote,
      wednesday: info.wednesday,
      wednesdayNote: info.wednesdayNote,
      thursday: info.thursday,
      thursdayNote: info.thursdayNote,
      friday: info.friday,
      fridayNote: info.fridayNote,
      saturday: info.saturday,
      saturdayNote: info.saturdayNote
    };

    reqBody[weekday] = Number(newVal.status);
    reqBody[`${weekday}Note`] = newVal.note;

    // this.IsLoading = true;
    const response = this.patientCheckListService.UpdatePatientAttendance(info.id, reqBody);
    response.subscribe({
      next: (res) => {
        if (res.result === 1) {
          this.GetPatientCheckList(this.PaginationParams);
          this.toastService.showToast({
            message: `Patient ${info.lastName} ${info.firstName}'s status updated for ${weekday}`,
            type: ToastType.Success
          });
        }
      },
      error: (err) => {
        console.log(err);
      },
      complete: () => {}
    });
  }

  /* Pagination */
  onPageChange(newPage: number): void {
    this.PaginationParams.pageNumber = newPage;
    this.GetPatientCheckList(this.PaginationParams);

    /* Just to maintain Pagination and Record Index */
    this.PaginationParams.skip = (newPage - 1) * this.PaginationParams.take;
  }

  onPageSizeChange(newPageSize: number): void {
    this.PaginationParams.pageSize = newPageSize;
    this.sharedService.setData('records', this.PaginationParams.take);
    this.GetPatientCheckList(this.PaginationParams);

    /* Just to maintain Pagination and Record Index */
    this.PaginationParams.take = newPageSize;
    this.PaginationParams.skip = 0;
  }
}
